<template>
  <aside class="learning-assistant" :aria-label="$t('learningAssistant.title')">
    <header class="assistant-header">
      <h2>{{ $t('learningAssistant.title') }}</h2>
      <button class="assistant-close" type="button" :aria-label="$t('common.close')" @click="$emit('close')">
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg>
      </button>
    </header>

    <Transition name="assistant-swap" mode="out-in">
      <p v-if="subtitle?.text" :key="subtitle.text" class="assistant-sentence">
        <template v-for="(part, index) in sentenceParts" :key="index">
          <mark v-if="part.marked">{{ part.text }}</mark>
          <template v-else>{{ part.text }}</template>
        </template>
      </p>
    </Transition>

    <nav class="assistant-tabs" :aria-label="$t('learningAssistant.title')">
      <button type="button" :class="{ active: type === 'assessment' }" @click="$emit('select-tab', 'assessment')">
        {{ $t('learningAssistant.pronunciation') }}
      </button>
      <button type="button" :class="{ active: type === 'explanation' }" @click="$emit('select-tab', 'explanation')">
        {{ $t('learningAssistant.explanation') }}
      </button>
    </nav>

    <div class="assistant-content">
      <Transition name="assistant-swap" mode="out-in">
      <section v-if="type === 'assessment'" :key="`assessment-${subtitle?.text}`" class="assessment-content">
        <template v-if="assessment?.status === 'done'">
          <div class="assessment-summary">
            <span>{{ assessmentText('feedback') }}</span>
            <strong>{{ assessmentSummary }}</strong>
            <small v-if="assessment.language">{{ assessmentText('assessmentLanguage') }}: {{ assessment.language }}</small>
          </div>

          <div class="assessment-score-grid">
            <div v-for="item in assessmentScoreItems" :key="item.key" class="score-item">
              <span>{{ item.label }}</span>
              <strong>{{ item.value ?? '-' }}</strong>
            </div>
          </div>

          <div v-if="weakWords.length" class="assessment-section">
            <span class="section-label">{{ assessmentText('problemWords') }}</span>
            <div class="word-list">
              <button
                v-for="word in weakWords"
                :key="`${word.word}-${word.accuracyScore}-${word.errorType}`"
                type="button"
                :class="{ active: selectedAssessmentWord === word }"
                @click="selectAssessmentWord(word)"
              >
                {{ displayAssessmentWord(word) }}
              </button>
            </div>
          </div>

          <div v-if="selectedAssessmentWord" class="word-detail">
            <div>
              <strong>{{ displayAssessmentWord(selectedAssessmentWord) }}</strong>
              <span>{{ assessmentErrorTypeLabel(selectedAssessmentWord.errorType) }}</span>
            </div>
            <button type="button" @click="playSelectedWord">
              <span aria-hidden="true">▶</span>
              {{ assessmentText('playWordPronunciation') }}
            </button>
          </div>

          <div v-if="assessment.recognizedText" class="assessment-section recognized-text">
            <span class="section-label">{{ assessmentText('recognizedText') }}</span>
            <p>{{ assessment.recognizedText }}</p>
          </div>
          <p v-if="assessmentBillingText" class="billing-text">{{ assessmentBillingText }}</p>
        </template>
        <p v-else class="assistant-empty">{{ $t('learningAssistant.noAssessment') }}</p>
      </section>

      <SentenceExplanationPanel
        v-else-if="explanation"
        :key="`explanation-${subtitle?.text}`"
        :state="explanation"
        :quota="explanationQuota"
        @generate="$emit('generate-explanation')"
        @ask-follow-up="$emit('ask-follow-up', $event)"
        @toggle-save="$emit('toggle-save-explanation')"
      />
      </Transition>
    </div>
  </aside>
</template>

<script>
import SentenceExplanationPanel from './SentenceExplanationPanel.vue'
import { playVocabularyAudio } from '@/utils/vocabularyAudio'

export default {
  name: 'LearningAssistantPanel',
  components: { SentenceExplanationPanel },
  emits: ['close', 'select-tab', 'generate-explanation', 'ask-follow-up', 'toggle-save-explanation'],
  props: {
    type: { type: String, default: 'explanation' },
    subtitle: { type: Object, default: null },
    assessment: { type: Object, default: null },
    explanation: { type: Object, default: null },
    explanationQuota: { type: Object, default: null },
  },
  data() {
    return { selectedAssessmentWord: null }
  },
  computed: {
    sentenceParts() {
      const text = this.subtitle?.text || ''
      const points = this.type === 'explanation' ? this.explanation?.entry?.explanation?.points || [] : []
      const ranges = points
        .map((point) => ({ start: text.indexOf(point.original), length: point.original.length }))
        .filter((range) => range.start >= 0)
        .sort((a, b) => a.start - b.start)
      const parts = []
      let cursor = 0
      ranges.forEach(({ start, length }) => {
        if (start < cursor) return
        if (start > cursor) parts.push({ text: text.slice(cursor, start), marked: false })
        parts.push({ text: text.slice(start, start + length), marked: true })
        cursor = start + length
      })
      if (cursor < text.length) parts.push({ text: text.slice(cursor), marked: false })
      return parts
    },
    assessmentScoreItems() {
      if (this.assessment?.status !== 'done') return []
      return [
        ['pronunciation', this.assessment.pronunciationScore],
        ['accuracy', this.assessment.accuracyScore],
        ['completeness', this.assessment.completenessScore],
        ['fluency', this.assessment.fluencyScore],
      ].map(([key, value]) => ({ key, value, label: this.assessmentText(key) }))
    },
    weakWords() {
      return (this.assessment?.words || [])
        .filter((word) => this.displayAssessmentWord(word))
        .filter((word) => word.errorType && word.errorType !== 'None' || Number(word.accuracyScore) < 70)
        .slice(0, 8)
    },
    assessmentSummary() {
      const issues = []
      if ((this.assessment?.completenessScore ?? 100) < 80) issues.push(this.assessmentText('issueCompleteness'))
      if ((this.assessment?.accuracyScore ?? 100) < 70) issues.push(this.assessmentText('issueAccuracy'))
      if ((this.assessment?.fluencyScore ?? 100) < 70) issues.push(this.assessmentText('issueFluency'))
      if (issues.length) return `${this.assessmentText('summaryMainIssues')}${issues.join(this.assessmentText('summarySeparator'))}${this.assessmentText('summarySentenceEnd')}`
      if ((this.assessment?.pronunciationScore ?? 0) >= 90) return this.assessmentText('summaryExcellent')
      return this.weakWords.length ? this.assessmentText('summaryWeakWords') : this.assessmentText('summaryGood')
    },
    assessmentBillingText() {
      if (Number.isInteger(this.assessment?.anonymousAttemptsRemaining)) return this.assessmentText('anonymousAttemptUsed')
      if ((this.assessment?.usageCharged || 0) > 0) return this.$t('audioPlayer.pronunciationAssessment.pointsCharged', { points: this.assessment.usageCharged })
      return ''
    },
  },
  methods: {
    playSelectedWord() {
      return playVocabularyAudio('', this.displayAssessmentWord(this.selectedAssessmentWord))
    },
    assessmentText(key) {
      return this.$t(`audioPlayer.pronunciationAssessment.${key}`)
    },
    displayAssessmentWord(word) {
      return String(word?.displayWord || word?.word || '').replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
    },
    assessmentErrorTypeLabel(errorType) {
      if (!errorType || errorType === 'None') return this.assessmentText('issueAccuracy')
      return this.$t(`audioPlayer.pronunciationAssessment.errorTypes.${errorType}`)
    },
    selectAssessmentWord(word) {
      this.selectedAssessmentWord = this.selectedAssessmentWord === word ? null : word
    },
  },
  watch: {
    assessment: {
      immediate: true,
      handler() {
        this.selectedAssessmentWord = this.weakWords[0] || null
      },
    },
  },
}
</script>

<style scoped>
/* 和右侧设置栏一样停靠在右边 */
.learning-assistant {
  position: absolute;
  top: var(--navbar-height);
  right: 0;
  bottom: 0;
  width: var(--learning-assistant-width, 380px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--color-text);
  background: var(--color-page);
  border-left: 1px solid var(--border-subtle);
  z-index: 120;
}

/* Panel slides in on the same spring curve the workspace uses to make room */
.learning-assistant.assistant-panel-enter-active { transition: transform var(--duration-panel) var(--ease-spring), opacity 260ms ease-out; }
.learning-assistant.assistant-panel-leave-active { transition: transform var(--duration-panel-exit) var(--ease-exit), opacity var(--duration-panel-exit) var(--ease-exit); }
.learning-assistant.assistant-panel-enter-from, .learning-assistant.assistant-panel-leave-to { opacity: 0; transform: translateX(28px); }

.assistant-swap-enter-active { transition: opacity 280ms ease-out, transform 360ms var(--ease-spring); }
.assistant-swap-leave-active { transition: opacity 140ms ease-in; }
.assistant-swap-enter-from { opacity: 0; transform: translateY(6px); }
.assistant-swap-leave-to { opacity: 0; }

.assistant-header { display: flex; align-items: center; justify-content: space-between; padding: var(--space-5) var(--space-6) 0; }
.assistant-header h2 { margin: 0; color: var(--color-text-subtle); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.02em; }
.assistant-close { width: 28px; height: 28px; margin-right: calc(var(--space-2) * -1); display: inline-flex; align-items: center; justify-content: center; padding: 0; color: var(--color-text-subtle); background: transparent; border: 0; border-radius: 50%; cursor: pointer; transition: background 160ms ease, color 160ms ease; }
.assistant-close svg { width: 12px; height: 12px; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; }
.assistant-close:hover { color: var(--color-text); background: var(--color-surface-muted); }

.assistant-sentence { margin: var(--space-3) var(--space-6) var(--space-6); color: var(--color-text); font-size: 1.28rem; font-weight: 600; line-height: 1.42; letter-spacing: -0.022em; text-wrap: pretty; }
.assistant-sentence mark { color: inherit; background: linear-gradient(transparent 64%, color-mix(in srgb, var(--signal-marker) 50%, transparent) 0); }

.assistant-tabs { display: flex; gap: var(--space-6); margin: 0 var(--space-6); border-bottom: 1px solid var(--color-border); }
.assistant-tabs button { position: relative; padding: 0 0 var(--space-3); color: var(--color-text-subtle); background: transparent; border: 0; cursor: pointer; font: inherit; font-size: 0.86rem; font-weight: 500; transition: color 200ms ease; }
.assistant-tabs button::after { content: ''; position: absolute; left: 0; right: 0; bottom: -1px; height: 2px; background: var(--color-text); transform: scaleX(0); transition: transform 320ms var(--ease-spring); }
.assistant-tabs button:hover, .assistant-tabs button.active { color: var(--color-text); }
.assistant-tabs button.active::after { transform: scaleX(1); }

.assistant-content { min-height: 0; flex: 1; overflow-y: auto; padding: var(--space-6); scrollbar-width: thin; }

.assessment-content { display: grid; gap: var(--space-6); }
.assessment-summary { display: grid; gap: var(--space-1); }
.assessment-summary > span, .section-label, .score-item span { color: var(--color-text-subtle); font-size: 0.75rem; font-weight: 500; }
.assessment-summary strong { font-size: 1.05rem; font-weight: 600; letter-spacing: -0.01em; }
.assessment-summary small { color: var(--color-text-subtle); font-size: 0.75rem; }
.assessment-score-grid { display: grid; grid-template-columns: 1fr 1fr; border-top: 1px solid var(--color-border); }
.score-item { display: grid; gap: 2px; padding: var(--space-3) 0; border-bottom: 1px solid var(--color-border); }
.score-item:nth-child(odd) { padding-right: var(--space-4); }
.score-item:nth-child(even) { padding-left: var(--space-4); border-left: 1px solid var(--color-border); }
.score-item strong { font-family: var(--font-family-data); font-size: 1.35rem; font-weight: 500; letter-spacing: -0.02em; }
.assessment-section { display: grid; gap: var(--space-2); }
.word-list { display: flex; flex-wrap: wrap; gap: var(--space-2); }
.word-list button { min-height: 32px; padding: 0 var(--space-3); color: var(--color-text-muted); background: transparent; border: 1px solid var(--color-border); border-radius: 6px; cursor: pointer; font: inherit; font-size: 0.82rem; transition: color 160ms ease, border-color 160ms ease; }
.word-list button:hover { color: var(--color-text); border-color: var(--color-border-strong); }
.word-list button.active { color: var(--color-brand-700); background: var(--color-brand-50); border-color: var(--color-brand-200); }
.word-detail { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); padding: var(--space-3) 0; border-top: 1px solid var(--color-border); border-bottom: 1px solid var(--color-border); }
.word-detail > div { display: grid; gap: 2px; }
.word-detail span { color: var(--color-text-subtle); font-size: 0.75rem; }
.word-detail button { display: inline-flex; align-items: center; gap: var(--space-2); padding: 0; color: var(--color-brand-700); background: transparent; border: 0; cursor: pointer; font: inherit; font-size: 0.8rem; font-weight: 500; }
.recognized-text p, .billing-text, .assistant-empty { margin: 0; color: var(--color-text-muted); font-size: 0.86rem; line-height: 1.6; }
.billing-text { color: var(--color-text-subtle); font-size: 0.75rem; }
button:focus-visible { outline: 2px solid var(--color-brand-700); outline-offset: 2px; }

@media (prefers-reduced-motion: reduce) {
  .learning-assistant.assistant-panel-enter-active, .learning-assistant.assistant-panel-leave-active { transition: opacity 120ms linear; }
  .learning-assistant.assistant-panel-enter-from, .learning-assistant.assistant-panel-leave-to { transform: none; }
  .assistant-swap-enter-active, .assistant-swap-leave-active, .assistant-close, .assistant-tabs button, .assistant-tabs button::after, .word-list button { transition: none; }
  .assistant-swap-enter-from { transform: none; }
}
@media (max-width: 1100px) {
  .learning-assistant { position: fixed; top: calc(var(--navbar-height) + var(--space-3)); right: var(--space-3); bottom: var(--space-3); width: min(400px, calc(100vw - var(--space-6))); background: var(--surface-panel); border: 1px solid var(--border-default); border-radius: var(--radius-lg); box-shadow: var(--shadow-float); }
}
@media (max-width: 640px) {
  .learning-assistant { top: auto; left: var(--space-2); right: var(--space-2); bottom: var(--space-2); width: auto; max-height: 72dvh; }
  .learning-assistant.assistant-panel-enter-from, .learning-assistant.assistant-panel-leave-to { transform: translateY(32px); }
  .assistant-header { padding: var(--space-4) var(--space-4) 0; }
  .assistant-sentence { margin: var(--space-2) var(--space-4) var(--space-5); font-size: 1.12rem; }
  .assistant-tabs { margin-inline: var(--space-4); }
  .assistant-content { padding: var(--space-5) var(--space-4); }
}
</style>
